import SubdirectoryArrowLeftIcon from '@material-ui/icons/SubdirectoryArrowLeft';
import { default as useOrgSettingsStyles } from '../styles';
import { useGetGroupsQuery } from '@/serverStore/account/queries';
import { GroupUser } from '@clef/shared/types';
import MaterialTable, { Column } from '@material-table/core';
import React, { useMemo, useState } from 'react';
import SearchBar from './SearchBar';
import { Box, Divider, MenuItem, Popover, Tooltip, Typography } from '@material-ui/core';
import { Chip } from '../components/Chip';
import { tableStyleOptions } from '../styles';
import { Button } from '@clef/client-library';
import { makeStyles } from '@material-ui/core/styles';
import GroupMembersDialog from './GroupMembersDialog';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import UserProfile from '../components/UserProfile';
import { emptyGroups } from '@clef/shared/utils';

const useStyles = makeStyles(theme => ({
  searchResults: {
    width: 300,
    height: 'auto',
  },
  groupSearchResultItem: {
    display: 'block',
    width: '100%',
    height: 'auto',
    textAlign: 'left',
  },
  groupSearchNonInteractive: {
    padding: theme.spacing(1, 2),
  },
  searchForGroupNameContainer: {
    display: 'block grid',
    padding: theme.spacing(1, 2),
    width: '100%',
    gridTemplateColumns: '1fr auto',
  },
  searchForGroupNameLabel: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(20),
  },
  membersCell: {
    '&:hover $viewMembersButton': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  viewMembersButton: {
    marginLeft: theme.spacing(1),
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    visibility: 'hidden',
  },
}));

const GroupsTable: React.FC<{}> = () => {
  const { data: groups = emptyGroups } = useGetGroupsQuery();
  const users = useTypedSelector(state => state.user.users);
  const [searchType, setSearchType] = useState<'' | 'group_name' | 'member_email'>('');
  const [searchText, setSearchText] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const styles = useStyles();
  const orgSettingsStyles = useOrgSettingsStyles();
  const [selectedGroup, setSelectedGroup] = useState<GroupUser | null | undefined>(null);

  const renderGroups = useMemo(() => {
    return groups.filter(group => {
      if (searchType === 'group_name') {
        return group.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());
      } else if (searchType === 'member_email' && searchText) {
        return group.members?.some(member =>
          member.email.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
        );
      }
      return true;
    });
  }, [groups, searchText, searchType]);

  const MAX_USERS_TO_SHOW = 3;

  const matchingUsers = useMemo(() => {
    return users
      .filter(user => user.email.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))
      .slice(0, MAX_USERS_TO_SHOW);
  }, [users, searchText]);

  const columns: Column<GroupUser>[] = useMemo(() => {
    return [
      { title: t('Id'), field: 'id', hidden: true },
      {
        title: (
          <SearchBar
            searchText={searchText}
            placeholder={t('Search groups')}
            onChange={event => {
              const { value } = event.currentTarget;
              setSearchText(value);
              if (!value) setSearchType('');
              setAnchorEl(value ? event.currentTarget : null);
            }}
          />
        ),
        width: 200,
        render: item => item.name,
        customSort: (a, b) => {
          const nameA = a.name;
          const nameB = b.name;
          return nameA.localeCompare(nameB);
        },
      },
      {
        title: t('Members'),
        width: 500,
        render: item => {
          if (!item.members) {
            return (
              <Tooltip title={t('Only group members can see the member list')}>
                <span>--</span>
              </Tooltip>
            );
          }
          if (item.members.length === 0) {
            return t('No members');
          }

          let memberNames = '';
          const firstMemberNames = item.members
            .slice(0, 3)
            .map(item => [item.name, item.lastName].filter(Boolean).join(' '));
          const numberOfRestMembers = item.members.length - 3;
          if (numberOfRestMembers <= 0) {
            memberNames = firstMemberNames.join(', ');
          } else {
            memberNames = t('{{firstMemberNames}} and {{numberOfRestMembers}} others', {
              firstMemberNames: firstMemberNames.join(', '),
              numberOfRestMembers,
            });
          }
          return (
            <Box display="flex" alignItems="center" className={styles.membersCell}>
              <span>{memberNames}</span>
              <Button
                id={`org-settings-groups-table-member-button-${item.id}`}
                variant="text"
                size="small"
                color="primary"
                className={styles.viewMembersButton}
                onClick={() => setSelectedGroup(item)}
              >
                {t('View Members')}
              </Button>
            </Box>
          );
        },
        sorting: false,
      },
      {
        title: t('Status'),
        width: 100,
        render: () => <Chip text={t('Active')} color="green" />,
        sorting: false,
      },
    ];
  }, [searchText, styles]);

  return (
    <>
      <MaterialTable<GroupUser>
        columns={columns}
        data={renderGroups}
        options={{
          ...tableStyleOptions,
          search: false,
          toolbar: false,
          padding: 'dense',
        }}
      />
      <GroupMembersDialog group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 300,
          },
        }}
        disableAutoFocus
      >
        <MenuItem
          id="search-group-by-name"
          onClick={() => {
            setAnchorEl(null);
            setSearchType('group_name');
          }}
          className={styles.groupSearchResultItem}
        >
          <Box className={styles.searchForGroupNameContainer}>
            <Typography style={{ fontWeight: 600 }}>
              {t('Find groups named "{{searchText}}"', { searchText })}
            </Typography>
            <SubdirectoryArrowLeftIcon />
          </Box>
        </MenuItem>
        <Divider className={orgSettingsStyles.divider} />
        <Box className={styles.groupSearchNonInteractive}>
          <Typography style={{ fontWeight: 'bold' }}>
            {t('Or find groups by member email address')}
          </Typography>
        </Box>
        {matchingUsers.length > 0 ? (
          matchingUsers.map(user => (
            <MenuItem
              key={user.id}
              className={styles.groupSearchResultItem}
              onClick={() => {
                setSearchText(user.email);
                setAnchorEl(null);
                setSearchType('member_email');
              }}
            >
              <UserProfile item={user} />
            </MenuItem>
          ))
        ) : (
          <Box className={styles.groupSearchNonInteractive}>
            <Typography style={{ fontStyle: 'italic' }}>{t('No users found')}</Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default GroupsTable;
